import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";
import {
  Zap,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Flame,
  Search,
  SlidersHorizontal,
  Smartphone,
  Gamepad2,
  Layers,
  ChevronRight,
} from "lucide-react";

export const KyuStoreMotionShowcase: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Color tokens (SaaS Minimalist Tech Palette)
  const bgCanvas = "#F8F9FA";
  const textDark = "#111827";
  const textMuted = "#6B7280";
  const brandBlue = "#0062FF";
  const brandEmerald = "#10B981";
  const cardBg = "#FFFFFF";

  // Helper spring
  const sp = (delay: number, damping = 16, stiffness = 140, mass = 0.9) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass },
    });

  // Top static header (fades in at frame 5)
  const headerOpacity = interpolate(frame, [0, 15], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgCanvas,
        color: textDark,
        fontFamily: "'Space Grotesk', 'Inter', -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 0. AUDIO TRACKS (Ref Beat + Micro SFX) */}
      <Audio src={staticFile("ref_beat.mp3")} volume={0.85} />

      {/* SFX triggers on scene transitions */}
      {frame >= 0 && frame < 5 && <Audio src={staticFile("click-soft.mp3")} volume={0.6} />}
      {frame >= 135 && frame < 140 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.7} />}
      {frame >= 285 && frame < 290 && <Audio src={staticFile("ui-pop.mp3")} volume={0.7} />}
      {frame >= 465 && frame < 470 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.7} />}
      {frame >= 615 && frame < 620 && <Audio src={staticFile("pop.mp3")} volume={0.6} />}
      {frame >= 735 && frame < 740 && <Audio src={staticFile("ui-success.mp3")} volume={0.8} />}

      {/* 1. PERSISTENT TOP HEADER (BRAND LOCKUP) */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: 60,
          right: 60,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 100,
          opacity: headerOpacity,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              backgroundColor: brandBlue,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 16px rgba(0, 98, 255, 0.25)",
            }}
          >
            <Zap size={24} color="#FFFFFF" strokeWidth={2.8} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em" }}>
            kyustore <span style={{ fontWeight: 400, color: textMuted }}>/ web</span>
          </div>
        </div>

        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: textMuted,
            textTransform: "uppercase",
          }}
        >
          BUILT FOR YOUR NEXT MATCH
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────
          SCENE 1 (Frame 0 - 135 / 0.0s - 4.5s): MINIMALIST HOOK & TYPEWRITER
      ─────────────────────────────────────────────────────────────────── */}
      {frame < 140 && (
        <Scene1Hook
          frame={frame}
          fps={fps}
          brandBlue={brandBlue}
          textDark={textDark}
          textMuted={textMuted}
        />
      )}

      {/* ─────────────────────────────────────────────────────────────────
          SCENE 2 (Frame 130 - 290 / 4.3s - 9.7s): 3D PERSPECTIVE BROWSER TILT
      ─────────────────────────────────────────────────────────────────── */}
      {frame >= 130 && frame < 290 && (
        <Scene2BrowserTilt
          frame={frame - 130}
          fps={fps}
          brandBlue={brandBlue}
          textDark={textDark}
          textMuted={textMuted}
        />
      )}

      {/* ─────────────────────────────────────────────────────────────────
          SCENE 3 (Frame 280 - 470 / 9.3s - 15.7s): FEATURED CAROUSEL SHOWCASE
      ─────────────────────────────────────────────────────────────────── */}
      {frame >= 280 && frame < 470 && (
        <Scene3Carousel
          frame={frame - 280}
          fps={fps}
          brandBlue={brandBlue}
          textDark={textDark}
          textMuted={textMuted}
        />
      )}

      {/* ─────────────────────────────────────────────────────────────────
          SCENE 4 (Frame 460 - 620 / 15.3s - 20.7s): FANNED CARDS DECK 2.5D
      ─────────────────────────────────────────────────────────────────── */}
      {frame >= 460 && frame < 620 && (
        <Scene4FannedDeck
          frame={frame - 460}
          fps={fps}
          brandBlue={brandBlue}
          textDark={textDark}
          textMuted={textMuted}
        />
      )}

      {/* ─────────────────────────────────────────────────────────────────
          SCENE 5 (Frame 610 - 735 / 20.3s - 24.5s): 3x3 CASCADE CATALOG GRID
      ─────────────────────────────────────────────────────────────────── */}
      {frame >= 610 && frame < 735 && (
        <Scene5CascadeGrid
          frame={frame - 610}
          fps={fps}
          brandBlue={brandBlue}
          textDark={textDark}
          textMuted={textMuted}
        />
      )}

      {/* ─────────────────────────────────────────────────────────────────
          SCENE 6 (Frame 725 - 840 / 24.2s - 28.0s): OUTRO & PILL CTA
      ─────────────────────────────────────────────────────────────────── */}
      {frame >= 725 && (
        <Scene6Outro
          frame={frame - 725}
          fps={fps}
          brandBlue={brandBlue}
          textDark={textDark}
          textMuted={textMuted}
        />
      )}
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS FOR EACH SCENE
// ─────────────────────────────────────────────────────────────────────────────

interface SceneProps {
  frame: number;
  fps: number;
  brandBlue: string;
  textDark: string;
  textMuted: string;
}

/**
 * SCENE 1: HOOK & TYPEWRITER URL BAR
 */
const Scene1Hook: React.FC<SceneProps> = ({ frame, fps, brandBlue, textDark, textMuted }) => {
  const intro = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 120 },
  });

  const exitOpacity = interpolate(frame, [125, 140], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Typewriter effect
  const fullUrl = "www.kyustore.com";
  const typedCount = Math.min(
    fullUrl.length,
    Math.max(0, Math.floor((frame - 20) * 0.45))
  );
  const typedText = fullUrl.slice(0, typedCount);
  const showCursor = frame >= 15 && frame % 16 < 8;

  return (
    <div
      style={{
        position: "absolute",
        top: 240,
        left: 0,
        right: 0,
        bottom: 240,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "0 80px",
        opacity: exitOpacity,
        transform: `translateY(${interpolate(intro, [0, 1], [40, 0])}px)`,
      }}
    >
      {/* Eyebrow */}
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          letterSpacing: "0.16em",
          color: textMuted,
          textTransform: "uppercase",
          marginBottom: 20,
        }}
      >
        GOOD GAMING STARTS HERE
      </div>

      {/* Headline */}
      <div
        style={{
          fontSize: 82,
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 800,
          lineHeight: 1.05,
          letterSpacing: "-0.03em",
          color: textDark,
          marginBottom: 48,
        }}
      >
        Mau mabar seru?
        <br />
        <span style={{ color: brandBlue }}>Mulai dari sini.</span>
      </div>

      {/* Floating Pill Input with Typewriter */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: 9999,
          padding: "20px 32px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          boxShadow: "0 24px 50px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0,0,0,0.03)",
          border: "1px solid rgba(0, 0, 0, 0.06)",
          width: "100%",
          maxWidth: 700,
          marginBottom: 40,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            backgroundColor: brandBlue,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Zap size={22} color="#FFFFFF" strokeWidth={3} />
        </div>
        <div
          style={{
            fontSize: 32,
            fontFamily: "'Space Grotesk', monospace",
            fontWeight: 700,
            color: textDark,
            display: "flex",
            alignItems: "center",
            letterSpacing: "-0.01em",
          }}
        >
          {typedText}
          <span
            style={{
              display: "inline-block",
              width: 3,
              height: 36,
              backgroundColor: brandBlue,
              marginLeft: 4,
              opacity: showCursor ? 1 : 0,
            }}
          />
        </div>
      </div>

      {/* Categories Subtext */}
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: textMuted,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span>Mobile Legends</span>
        <span style={{ color: "#D1D5DB" }}>·</span>
        <span>Free Fire</span>
        <span style={{ color: "#D1D5DB" }}>·</span>
        <span>Valorant</span>
        <span style={{ color: "#D1D5DB" }}>·</span>
        <span>FC Mobile</span>
      </div>
    </div>
  );
};

/**
 * SCENE 2: 3D PERSPECTIVE BROWSER MOCKUP TILT
 */
const Scene2BrowserTilt: React.FC<SceneProps> = ({ frame, fps, brandBlue, textDark, textMuted }) => {
  const enterSpring = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 130 },
  });

  const exitOpacity = interpolate(frame, [145, 160], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // 3D Perspective Rotation animation (tilts in then settles)
  const rotX = interpolate(enterSpring, [0, 1], [18, 5]);
  const rotY = interpolate(enterSpring, [0, 1], [-8, 0]);
  const transY = interpolate(enterSpring, [0, 1], [100, 0]);
  const scale = interpolate(enterSpring, [0, 1], [0.88, 1]);

  return (
    <div
      style={{
        position: "absolute",
        top: 190,
        left: 0,
        right: 0,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 60px",
        opacity: exitOpacity,
      }}
    >
      {/* Scene Headline */}
      <div style={{ width: "100%", marginBottom: 32 }}>
        <div
          style={{
            fontSize: 56,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: "-0.03em",
            color: textDark,
            marginBottom: 8,
          }}
        >
          Top-up berikutnya, <span style={{ color: brandBlue }}>mulai di sini.</span>
        </div>
        <div style={{ fontSize: 24, fontWeight: 600, color: textMuted }}>
          0.8 detik mendarat otomatis, tanpa login password.
        </div>
      </div>

      {/* 3D Browser Container */}
      <div
        style={{
          width: "100%",
          perspective: 1400,
          transformStyle: "preserve-3d",
          flex: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            backgroundColor: "#FFFFFF",
            borderRadius: 28,
            boxShadow: "0 40px 90px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0,0,0,0.04)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            transform: `translateY(${transY}px) scale(${scale}) rotateX(${rotX}deg) rotateY(${rotY}deg)`,
            transformOrigin: "center top",
          }}
        >
          {/* Browser Chrome Header */}
          <div
            style={{
              padding: "18px 24px",
              backgroundColor: "#FAFAFA",
              borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Mac Traffic Dots */}
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FF5F56" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#FFBD2E" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#27C93F" }} />
            </div>

            {/* Address Bar */}
            <div
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: 9999,
                padding: "6px 20px",
                fontSize: 16,
                fontWeight: 600,
                color: textMuted,
                border: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ color: brandBlue }}>https://</span>
              <span>kyustore.com/games</span>
            </div>

            <ArrowUpRight size={18} color={textMuted} />
          </div>

          {/* Website Mockup Content */}
          <div style={{ padding: "32px 32px", display: "flex", flexDirection: "column", flex: 1, backgroundColor: "#FFFFFF" }}>
            {/* Mockup Mini Nav */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: brandBlue }}>
                KYU<span style={{ color: textDark }}>STORE</span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 16, fontWeight: 700, color: textMuted }}>
                <span style={{ color: brandBlue }}>Katalog</span>
                <span>Promo QRIS</span>
                <span>Cek ID</span>
              </div>
            </div>

            {/* Mockup Hero Banner */}
            <div
              style={{
                backgroundColor: "#F3F7FF",
                borderRadius: 20,
                padding: "24px 28px",
                marginBottom: 24,
                border: "1px solid rgba(0, 98, 255, 0.12)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: brandBlue, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  FLASH SPEED INJECT 0.8s
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, color: textDark }}>
                  Top Up Game Sat-Set
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: textMuted }}>
                  Tanpa ribet daftar, scan QRIS langsung masuk.
                </div>
              </div>
              <div
                style={{
                  backgroundColor: brandBlue,
                  color: "#FFFFFF",
                  padding: "10px 20px",
                  borderRadius: 9999,
                  fontSize: 16,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Top Up <ArrowRight size={16} />
              </div>
            </div>

            {/* Game Cards Row Preview inside Mockup */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, flex: 1 }}>
              <GameThumbCard
                title="Mobile Legends"
                category="Bang Bang"
                badge="POPULER"
                bg="#EBF3FF"
              />
              <GameThumbCard
                title="Free Fire"
                category="Garena"
                badge="AUTO INJECT"
                bg="#FFF4EB"
              />
              <GameThumbCard
                title="Valorant"
                category="Riot Games"
                badge="INSTANT VP"
                bg="#F3EBFB"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GameThumbCard: React.FC<{ title: string; category: string; badge: string; bg: string }> = ({
  title,
  category,
  badge,
  bg,
}) => (
  <div
    style={{
      backgroundColor: bg,
      borderRadius: 16,
      padding: "20px 16px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      border: "1px solid rgba(0, 0, 0, 0.05)",
    }}
  >
    <div
      style={{
        display: "inline-block",
        backgroundColor: "#FFFFFF",
        padding: "4px 10px",
        borderRadius: 9999,
        fontSize: 11,
        fontWeight: 800,
        color: "#111827",
        width: "fit-content",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      }}
    >
      {badge}
    </div>
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#6B7280" }}>{category}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>{title}</div>
    </div>
  </div>
);

/**
 * SCENE 3: FEATURED CAROUSEL SHOWCASE (WEEKLY PASS & MLBB)
 */
const Scene3Carousel: React.FC<SceneProps> = ({ frame, fps, brandBlue, textDark, textMuted }) => {
  const enterSpring = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const exitOpacity = interpolate(frame, [170, 185], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Carousel slider animation
  const cardTranslateX = interpolate(enterSpring, [0, 1], [140, 0]);

  return (
    <div
      style={{
        position: "absolute",
        top: 200,
        left: 0,
        right: 0,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        padding: "0 60px",
        opacity: exitOpacity,
      }}
    >
      {/* Header with Category Link */}
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            fontSize: 56,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: "-0.03em",
            color: textDark,
            marginBottom: 10,
          }}
        >
          Beda game. <span style={{ color: brandBlue }}>Banyak pilihan.</span>
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: brandBlue,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Instant Top-Up Experience <ArrowUpRight size={22} strokeWidth={2.8} />
        </div>
      </div>

      {/* Featured Big Showcase Card */}
      <div
        style={{
          width: "100%",
          flex: 1,
          backgroundColor: "#FFFFFF",
          borderRadius: 32,
          boxShadow: "0 36px 80px rgba(0, 0, 0, 0.09), 0 6px 18px rgba(0,0,0,0.03)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transform: `translateX(${cardTranslateX}px)`,
          position: "relative",
        }}
      >
        {/* Visual Hero Area */}
        <div
          style={{
            height: "56%",
            backgroundColor: "#0D1829",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Img
            src={staticFile("assets_library/heroes/gusion_night_owl.png")}
            style={{
              height: "112%",
              objectFit: "contain",
              transform: "translateY(6px)",
              filter: "contrast(1.1) drop-shadow(0 20px 40px rgba(0,0,0,0.8))",
            }}
          />

          {/* Floating Pill on Artwork */}
          <div
            style={{
              position: "absolute",
              top: 28,
              left: 28,
              backgroundColor: "rgba(255, 255, 255, 0.94)",
              backdropFilter: "blur(12px)",
              borderRadius: 9999,
              padding: "10px 22px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
            }}
          >
            <Zap size={18} color={brandBlue} strokeWidth={3} />
            <span style={{ fontSize: 16, fontWeight: 800, color: textDark }}>0.8s AUTO INJECT</span>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: 24,
              right: 28,
              backgroundColor: "rgba(0, 0, 0, 0.65)",
              backdropFilter: "blur(12px)",
              borderRadius: 14,
              padding: "8px 18px",
              color: "#FFFFFF",
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            MLBB Official Partner
          </div>
        </div>

        {/* Card Body Details */}
        <div style={{ padding: "32px 36px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: brandBlue, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
              BEST SELLER PAKET
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: textDark, marginBottom: 8 }}>
              Weekly Diamond Pass (WDP)
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: textMuted, lineHeight: 1.3 }}>
              Dapat hingga 210 Diamond + 210 Starlight Point. Hemat 60% dibanding top-up reguler!
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: textMuted }}>Harga Termurah</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: brandBlue }}>Rp 27.500</div>
            </div>

            <div
              style={{
                backgroundColor: textDark,
                color: "#FFFFFF",
                padding: "14px 28px",
                borderRadius: 9999,
                fontSize: 18,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              Order Instant <ArrowRight size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* Carousel Pill Pagination Dots */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 24 }}>
        <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#D1D5DB" }} />
        <div style={{ width: 44, height: 12, borderRadius: 9999, backgroundColor: brandBlue }} />
        <div style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "#D1D5DB" }} />
      </div>
    </div>
  );
};

/**
 * SCENE 4: FANNED CARDS DECK 2.5D (KARTU MEKAR MELAYANG)
 */
const Scene4FannedDeck: React.FC<SceneProps> = ({ frame, fps, brandBlue, textDark, textMuted }) => {
  const enterSpring = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 120 },
  });

  const exitOpacity = interpolate(frame, [145, 160], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Staggered fan angles
  const rotLeft = interpolate(enterSpring, [0, 1], [0, -10]);
  const transXLeft = interpolate(enterSpring, [0, 1], [0, -90]);

  const rotRight = interpolate(enterSpring, [0, 1], [0, 10]);
  const transXRight = interpolate(enterSpring, [0, 1], [0, 90]);

  const scaleCenter = interpolate(enterSpring, [0, 1], [0.92, 1.04]);

  return (
    <div
      style={{
        position: "absolute",
        top: 200,
        left: 0,
        right: 0,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 60px",
        opacity: exitOpacity,
      }}
    >
      {/* Headline */}
      <div style={{ width: "100%", marginBottom: 36 }}>
        <div
          style={{
            fontSize: 56,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: "-0.03em",
            color: textDark,
            marginBottom: 8,
          }}
        >
          Satu web. <span style={{ color: brandBlue }}>Banyak kemudahan.</span>
        </div>
        <div style={{ fontSize: 24, fontWeight: 600, color: textMuted }}>
          Didesain tanpa celah friksi untuk pengalaman top-up paling mulus.
        </div>
      </div>

      {/* Fanned Cards Stage */}
      <div
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Card Left: Anti-Minus Garansi */}
        <div
          style={{
            position: "absolute",
            width: 440,
            height: 520,
            backgroundColor: "#FFFFFF",
            borderRadius: 28,
            padding: 36,
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.07)",
            transform: `translateX(${transXLeft}px) translateY(20px) rotate(${rotLeft}deg)`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              backgroundColor: "#ECFDF5",
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShieldCheck size={32} color="#10B981" strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#10B981", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              100% LEGAL & AMAN
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, color: textDark, marginTop: 6, lineHeight: 1.15 }}>
              Anti-Minus Garansi Seumur Hidup
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: textMuted, marginTop: 12 }}>
              Jalur distribusi server resmi publisher, akun dijamin aman tanpa drama.
            </div>
          </div>
        </div>

        {/* Card Right: 24/7 Fast Response CS */}
        <div
          style={{
            position: "absolute",
            width: 440,
            height: 520,
            backgroundColor: "#FFFFFF",
            borderRadius: 28,
            padding: 36,
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.07)",
            transform: `translateX(${transXRight}px) translateY(20px) rotate(${rotRight}deg)`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              backgroundColor: "#FFF7ED",
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Clock size={32} color="#F97316" strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#F97316", textTransform: "uppercase", letterSpacing: "0.1em" }}>
              STANDBY NON-STOP
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, color: textDark, marginTop: 6, lineHeight: 1.15 }}>
              Layanan CS 24 Jam Sigap
            </div>
            <div style={{ fontSize: 18, fontWeight: 600, color: textMuted, marginTop: 12 }}>
              Ada kendala tengah malam? Tim admin siap bantu detik itu juga.
            </div>
          </div>
        </div>

        {/* Center Hero Card: QRIS Instant 0.8 Detik */}
        <div
          style={{
            position: "absolute",
            width: 470,
            height: 560,
            backgroundColor: brandBlue,
            borderRadius: 32,
            padding: 40,
            boxShadow: "0 36px 90px rgba(0, 98, 255, 0.35)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            transform: `scale(${scaleCenter})`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            zIndex: 20,
            color: "#FFFFFF",
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              backgroundColor: "#FFFFFF",
              borderRadius: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
            }}
          >
            <Zap size={36} color={brandBlue} strokeWidth={3} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#93C5FD", textTransform: "uppercase", letterSpacing: "0.14em" }}>
              AUTOMATED API ENGINE
            </div>
            <div style={{ fontSize: 40, fontWeight: 900, color: "#FFFFFF", marginTop: 8, lineHeight: 1.1 }}>
              Scan QRIS, 0.8 Detik Masuk
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, color: "#DBEAFE", marginTop: 14 }}>
              Cukup tempel User ID, scan QRIS dari BCA/Gopay/Dana/OVO, saldo langsung mendarat.
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 18,
              fontWeight: 800,
              color: "#FFFFFF",
            }}
          >
            Jelajahi Fitur <ArrowRight size={20} />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * SCENE 5: 3x3 CASCADE CATALOG GRID
 */
const Scene5CascadeGrid: React.FC<SceneProps> = ({ frame, fps, brandBlue, textDark, textMuted }) => {
  const exitOpacity = interpolate(frame, [115, 125], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const CATALOG_ITEMS = [
    { title: "Mobile Legends", badge: "86 + 10 DM", tag: "MLBB", bg: "#EFF6FF" },
    { title: "Free Fire", badge: "140 Diamond", tag: "FF", bg: "#FEF2F2" },
    { title: "Valorant", badge: "1,000 VP", tag: "VALO", bg: "#FAF5FF" },
    { title: "Genshin Impact", badge: "Blessing Welkin", tag: "GENSHIN", bg: "#F0FDF4" },
    { title: "FC Mobile", badge: "500 FC Points", tag: "SOCCER", bg: "#FFFBEB" },
    { title: "Honkai: Star Rail", badge: "Express Pass", tag: "HSR", bg: "#ECFEFF" },
    { title: "PUBG Mobile", badge: "300 + 25 UC", tag: "BATTLE", bg: "#FFF7ED" },
    { title: "Call of Duty", badge: "420 CP", tag: "CODM", bg: "#F3F4F6" },
    { title: "Honor of Kings", badge: "80 Tokens", tag: "HOK", bg: "#FDF2F8" },
  ];

  return (
    <div
      style={{
        position: "absolute",
        top: 200,
        left: 0,
        right: 0,
        bottom: 80,
        display: "flex",
        flexDirection: "column",
        padding: "0 60px",
        opacity: exitOpacity,
      }}
    >
      {/* Headline */}
      <div style={{ marginBottom: 32 }}>
        <div
          style={{
            fontSize: 56,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 800,
            lineHeight: 1.08,
            letterSpacing: "-0.03em",
            color: textDark,
            marginBottom: 8,
          }}
        >
          Satu katalog. <span style={{ color: brandBlue }}>Semua game favorit.</span>
        </div>
        <div style={{ fontSize: 24, fontWeight: 600, color: textMuted }}>
          Pilih game, tempel ID, bayar receh tanpa minimal ribet.
        </div>
      </div>

      {/* 3x3 Grid of Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 18,
          flex: 1,
        }}
      >
        {CATALOG_ITEMS.map((item, idx) => {
          const itemSpring = spring({
            frame: frame - idx * 2.5,
            fps,
            config: { damping: 14, stiffness: 160 },
          });
          const scale = interpolate(itemSpring, [0, 1], [0.8, 1]);
          const opacity = interpolate(itemSpring, [0, 1], [0, 1]);

          return (
            <div
              key={idx}
              style={{
                backgroundColor: item.bg,
                borderRadius: 22,
                padding: "24px 20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0 14px 30px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                transform: `scale(${scale})`,
                opacity,
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#FFFFFF",
                  padding: "6px 12px",
                  borderRadius: 9999,
                  fontSize: 12,
                  fontWeight: 800,
                  color: brandBlue,
                  width: "fit-content",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                }}
              >
                {item.tag}
              </div>
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: textDark }}>{item.title}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: textMuted, marginTop: 4 }}>
                  {item.badge}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Prompt */}
      <div style={{ fontSize: 18, fontWeight: 700, color: textMuted, marginTop: 24, textAlign: "center" }}>
        Pilih koleksi. Lihat preview harga. Mulai main.
      </div>
    </div>
  );
};

/**
 * SCENE 6: OUTRO & PILL CTA
 */
const Scene6Outro: React.FC<SceneProps> = ({ frame, fps, brandBlue, textDark, textMuted }) => {
  const intro = spring({
    frame,
    fps,
    config: { damping: 16, stiffness: 140 },
  });

  const buttonSpring = spring({
    frame: frame - 12,
    fps,
    config: { damping: 14, stiffness: 150 },
  });

  return (
    <div
      style={{
        position: "absolute",
        top: 240,
        left: 0,
        right: 0,
        bottom: 120,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "0 80px",
        transform: `translateY(${interpolate(intro, [0, 1], [40, 0])}px)`,
        opacity: intro,
      }}
    >
      {/* Brand Lockup Big */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 40 }}>
        <div
          style={{
            width: 68,
            height: 68,
            backgroundColor: brandBlue,
            borderRadius: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 12px 28px rgba(0, 98, 255, 0.3)",
          }}
        >
          <Zap size={38} color="#FFFFFF" strokeWidth={3} />
        </div>
        <div style={{ fontSize: 44, fontWeight: 900, color: textDark, letterSpacing: "-0.03em" }}>
          kyustore <span style={{ fontWeight: 400, color: textMuted }}>/ official</span>
        </div>
      </div>

      {/* Dominant Headline */}
      <div
        style={{
          fontSize: 74,
          fontFamily: "'Space Grotesk', sans-serif",
          fontWeight: 800,
          lineHeight: 1.05,
          letterSpacing: "-0.03em",
          color: textDark,
          marginBottom: 20,
        }}
      >
        Top-up berikutnya,
        <br />
        <span style={{ color: brandBlue }}>berangkat dari sini.</span>
      </div>

      <div style={{ fontSize: 26, fontWeight: 600, color: textMuted, marginBottom: 56 }}>
        Temukan harga terbaik buat game lo sekarang.
      </div>

      {/* Wide Full-Width Pill CTA Button */}
      <div
        style={{
          width: "100%",
          maxWidth: 680,
          backgroundColor: brandBlue,
          color: "#FFFFFF",
          borderRadius: 9999,
          padding: "24px 44px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 28px 60px rgba(0, 98, 255, 0.35)",
          transform: `scale(${interpolate(buttonSpring, [0, 1], [0.92, 1])})`,
          opacity: buttonSpring,
          marginBottom: 32,
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.01em" }}>
          Jelajahi katalog game
        </div>
        <ArrowRight size={28} strokeWidth={3} />
      </div>

      {/* Direct Link Tag */}
      <div
        style={{
          fontSize: 24,
          fontWeight: 800,
          color: textDark,
          display: "flex",
          alignItems: "center",
          gap: 6,
          textDecoration: "underline",
          textUnderlineOffset: 6,
        }}
      >
        kyustore.com <ArrowUpRight size={22} strokeWidth={3} color={brandBlue} />
      </div>
    </div>
  );
};
